import React, { useState, useRef, useEffect, useContext } from 'react';
import { Container, Row, Col, Image, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Toast } from 'primereact/toast';
import JSEncrypt from 'jsencrypt';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMessageSmile } from '@fortawesome/pro-solid-svg-icons';

// Components
import ThemeSwitcher from '../../../../components/theme-switcher/ThemeSwitcher';
import SmsVerification from '../components/SmsVerification';
import RegisterStep1 from '../components/RegisterStep1';
import RegisterStep2 from '../components/RegisterStep2';
import validations from '../helpers/validations';

// API
import { DashboardApi } from '../../../../services/authentication/api';
import { Configuration } from '../../../../services/authentication/configuration';
import { setRegisterInputs } from '../../../../stores/registerSlice';
import { cityItems } from '../helpers/locationData';

// Assets
import logoLight from '../../../../assets/img/logo/main-logo-light.svg';
import logoDark from '../../../../assets/img/logo/main-logo-dark.svg';
import { ThemeContext } from '../../../../context/theme-context';
import captchaBg from '../../../../assets/img/texture-bg/texture-bg.png';
import '../Register.scss';

// Public key for encryption
const MetKey = `-----BEGIN PUBLIC KEY-----
MIIBITANBgkqhkiG9w0BAQEFAAOCAQ4AMIIBCQKCAQB3prLK5rWvXhOfC8JyMImg
NWH1XaY8+pQGs5FVy0dAPcogNi7FJKQvjNOx2nNl4ESU1ShT3TNe69L8x45MR2zg
OGbgN1NXdCCYu7bsJlQUxWuu1jEqZVl5PKgz8JSrkk47kRUFHw2Ogb50nYhR9X0q
LaU+UZshu5UG/ut/gaSS8TStrHwGifYsVRJIytSldEWlUWtuh19dc7RHVhwd3nd5
lBqqJC3cy2va2rRjC0f/UwHCANfvOPMT1Kfe+9ygihQ8l9B199+kgzy1Z/loBRsr
mVttnyKF9qvjzXbk5ZaFznyAFOu0pdLf3Wqp0X7YPFV7K2tqBoLJACUpH2NSUDej
AgMBAAE=
-----END PUBLIC KEY-----`;

const MainRegister = () => {
  const toast = useRef(null);
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(MetKey);

  const dashboardApi = new DashboardApi(
    new Configuration({ basePath: process.env.REACT_APP_AUTHENTICATION_API_BASE_PATH })
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const registerInputs = useSelector((state) => state.register);

  const currentTheme = useContext(ThemeContext).theme;
  const logo = currentTheme === 'dark' ? logoDark : logoLight;

  // State
  const [localRegisterInputs, setLocalRegisterInputs] = useState({ ...registerInputs });
  const [currentStep, setCurrentStep] = useState(1);
  const [securityCode, setSecurityCode] = useState('');
  const [registerView, setRegisterView] = useState(true);
  const [smsVerificationView, setSmsVerificationView] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowPasswordAgain, setIsShowPasswordAgain] = useState(false);

  // Create captcha when component mounts or when step changes
  useEffect(() => {
    if (currentStep === 1) {
      createCaptcha();
    }
  }, [currentStep]);

  // Handle input change
  const handleChange = (nameOrEvent, value) => {
    // Support PrimeReact Dropdown's event signature
    if (typeof nameOrEvent === 'object' && nameOrEvent.target) {
      const { name, value } = nameOrEvent.target;
      if (name === 'city') {
        // Find the full city object by id
        const selectedCity = cityItems.find((item) => item.id === value);
        setLocalRegisterInputs((prev) => ({ ...prev, city: selectedCity }));
        if (fieldErrors.city) {
          setFieldErrors((prev) => ({ ...prev, city: false }));
        }
      } else {
        setLocalRegisterInputs((prev) => ({ ...prev, [name]: value }));
        if (fieldErrors[name]) {
          setFieldErrors((prev) => ({ ...prev, [name]: false }));
        }
      }
    } else {
      setLocalRegisterInputs((prev) => ({ ...prev, [nameOrEvent]: value }));
      if (fieldErrors[nameOrEvent]) {
        setFieldErrors((prev) => ({ ...prev, [nameOrEvent]: false }));
      }
    }
  };

  // Validate a specific field
  const validateField = (fieldName) => {
    const errors = validations.getValidationErrors(localRegisterInputs, currentStep);

    if (errors[fieldName]) {
      setFieldErrors({ ...fieldErrors, [fieldName]: errors[fieldName] });
      return false;
    } else {
      setFieldErrors({ ...fieldErrors, [fieldName]: null });
      return true;
    }
  };

  // Handle form submission for step 1
  const handleStep1Submit = (e) => {
    if (e) e.preventDefault();

    // Validate all fields
    const errors = validations.getValidationErrors(localRegisterInputs, 1);

    // Check captcha
    const captchaValid = validateCaptcha();
    if (!captchaValid) {
      errors.captcha = "Güvenlik kodunu yanlış girdiniz.";
    }

    // Check checkboxes
    if (!privacyPolicy) {
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail: "Lütfen Gizlilik sözleşmesini ve Kişisel Verilerin Korunması Hakkında Bilgilendirme Metnini onaylayınız.",
        closable: true,
        closeIcon: "pi pi-times"
      });
    }

    if (!termsAgreed) {
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail: "Lütfen reklam, promosyon, kampanya ve benzeri ticari konulara ilişkin elektronik ileti gönderilmesini onaylayınız.",
        closable: true,
        closeIcon: "pi pi-times"
      });
    }

    // Display errors
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0 || !privacyPolicy || !termsAgreed) {
      // Show error toasts for each field
      errorKeys.forEach(key => {
        toast.current.show({
          severity: "error",
          summary: "Hata",
          detail: errors[key],
          closable: true,
          closeIcon: "pi pi-times"
        });
      });

      // Update field errors state
      setFieldErrors(errors);
      return;
    }

    // If no errors, proceed to step 2
    setCurrentStep(2);
  };

  // Final registration submission
  const handleRegisterSubmit = () => {
    // Validate step 2 fields

    const errors = validations.getValidationErrors(localRegisterInputs, 2);

    // Display errors
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      // Show error toasts for each field
      errorKeys.forEach(key => {
        toast.current.show({
          severity: "error",
          summary: "Hata",
          detail: errors[key],
          closable: true,
          closeIcon: "pi pi-times"
        });
      });

      // Update field errors state
      setFieldErrors(errors);
      return;
    }

    // Prepare data for API
    const reqParams = {
      FingerPrint: "string",
      Name: localRegisterInputs.name,
      Surname: localRegisterInputs.surname,
      Email: localRegisterInputs.email,
      PhoneNumber: localRegisterInputs.phone?.replace(/\D/g, ""),
      Password: localRegisterInputs.password,
      CityCode: localRegisterInputs.city?.code || 0,
      ExpireDate: moment().add(30, 'days').format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
      Address: localRegisterInputs.address?.replace(/\n/g, ''),
      InterestedProducts: Array.isArray(localRegisterInputs.interestedProducts)
        ? localRegisterInputs.interestedProducts.map(item => item.code)
        : [],
      Amount: localRegisterInputs.investmentAmount?.code || "",
    };

    // Send SMS for verification
    toast.current.show({
      severity: "info",
      summary: "Bilgi",
      detail: "SMS gönderiliyor... Lütfen bekleyiniz."
    });

    dashboardApi.apiDashboardSendDemoSmsGet(
      reqParams.Email,
      reqParams.Name,
      reqParams.Surname,
      reqParams.PhoneNumber
    ).then((response) => {
      if (response.data.Status === "Ok") {
        toast.current.show({
          severity: "success",
          summary: "Başarılı",
          detail: "SMS gönderildi. Lütfen telefonunuza gelen kodu giriniz."
        });

        dispatch(setRegisterInputs(localRegisterInputs));

        setTimeout(() => {
          setRegisterView(false);
          setSmsVerificationView(true);
        }, 1000);
      } else {
        if (response.data.Error?.includes("0x6DC2CA")) {
          toast.current.show({
            severity: "error",
            summary: "SMS gönderilemedi.",
            detail: "Lütfen tekrar deneyiniz."
          });
        } else if (response.data.Error?.includes("0x0c457d")) {
          toast.current.show({
            severity: "error",
            summary: "SMS gönderilemedi.",
            detail: "5 ile başlayan 10 haneli Numara girilmeli."
          });
        } else if (response.data.Error?.includes("0xFF5733")) {
          toast.current.show({
            severity: "error",
            summary: "SMS gönderilemedi.",
            detail: "Bu bilgilere ait kayıt mevcuttur."
          });
        } else {
          toast.current.show({
            severity: "error",
            summary: "SMS gönderilemedi.",
            detail: response.data.Error || "Bir hata oluştu"
          });
        }
      }
    }).catch((error) => {
      toast.current.show({
        severity: "error",
        summary: "Kayıt işlemi başarısız",
        detail: "Detay: " + (error.message || "Bir hata oluştu")
      });
    });
  };

  // Go back to step 1
  const handleBackToStep1 = () => {
    setCurrentStep(1);
  };

  // Navigate to login page
  const loginRedirect = () => {
    localStorage.removeItem('selectedListBarItemIdWL');
    navigate("/auth/login");
  };

  // Handle password visibility
  const handleMouseDownPassword = () => {
    setIsShowPassword(true);
  };

  const handleMouseUpPassword = () => {
    setIsShowPassword(false);
  };

  const handleMouseDownPasswordAgain = () => {
    setIsShowPasswordAgain(true);
  };

  const handleMouseUpPasswordAgain = () => {
    setIsShowPasswordAgain(false);
  };

  // Validate captcha
  const validateCaptcha = () => {
    return securityCode === localRegisterInputs.captcha;
  };

  // Create captcha
  const createCaptcha = () => {
    let captchaDiv = document.getElementById('cpth-code-elm');
    if (!captchaDiv) return;

    captchaDiv.innerHTML = "";
    const charsArray = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ@!#$%^&*";
    const lengthOtp = 6;
    const captcha = [];

    for (let i = 0; i < lengthOtp; i++) {
      const index = Math.floor(Math.random() * charsArray.length);
      if (captcha.indexOf(charsArray[index]) === -1) {
        captcha.push(charsArray[index]);
      } else {
        i--;
      }
    }

    const canv = document.createElement("canvas");
    canv.width = captchaDiv.clientWidth;
    canv.height = captchaDiv.clientHeight;
    canv.style.borderRadius = "10px";
    // Set background
    canv.style.backgroundImage = `url(${captchaBg})`;

    const ctx = canv.getContext("2d");
    ctx.fillStyle = "transparent";
    ctx.fillRect(0, 0, canv.width, canv.height);
    ctx.font = "bold 20px Helvetica Neue";
    ctx.fillStyle = "#ffffff";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.borderRadius = "10px";
    ctx.translate(canv.width / 2, canv.height / 2);
    ctx.rotate(3 * Math.PI / 180);
    ctx.fillText(captcha.join(""), 0, 0);

    setSecurityCode(captcha.join(""));
    captchaDiv.appendChild(canv);
  };

  // Reload captcha
  const reloadCaptcha = () => {
    createCaptcha();
  };

  // Toggle selection of interested products
  const handleToggleSelect = (clickedItem) => {
    // Clone the currently selected list
    const newSelected = [...(localRegisterInputs.interestedProducts || [])];

    // Check if we already have this item
    const index = newSelected.findIndex((el) => el.id === clickedItem.id);
    if (index !== -1) {
      // Item is found, remove it
      newSelected.splice(index, 1);
    } else {
      // Item not found, add it
      newSelected.push(clickedItem);
    }

    // Update the state
    setLocalRegisterInputs({ ...localRegisterInputs, interestedProducts: newSelected });
  };

  return (
    <div id="registerPage">
      <Toast ref={toast} position="bottom-right" />
      <Container className="header-container">
        <Row className="header-container-row">
          <Col>
            <Image src={logo} fluid onClick={loginRedirect} />
          </Col>
          <Col>
            <div className='theme-btn'><ThemeSwitcher /></div>
          </Col>
        </Row>
      </Container>
      <Row className="headerInfo">
        <Col lg="12">
          <label>
            <strong>
              <FontAwesomeIcon icon={faMessageSmile} className="m-r-5" />
            </strong>{" "}
            Telefonunuza SMS doğrulama kodu gönderilecektir. Lütfen telefon numaranızın doğru olduğundan emin olunuz.
          </label>
        </Col>
      </Row>
      <Container className="mainContainer">
        <Row>
          {registerView && (
            <Col lg="5">
              {currentStep === 1 && (
                <RegisterStep1
                  localRegisterInputs={localRegisterInputs}
                  handleChange={handleChange}
                  fieldErrors={fieldErrors}
                  registerClickStep1={handleStep1Submit}
                  isChecked1={privacyPolicy}
                  setIsChecked1={setPrivacyPolicy}
                  isChecked2={termsAgreed}
                  setIsChecked2={setTermsAgreed}
                  isShowPassword={isShowPassword}
                  handleMouseDownPassword={handleMouseDownPassword}
                  handleMouseUpPassword={handleMouseUpPassword}
                  isShowPasswordAgain={isShowPasswordAgain}
                  handleMouseDownPasswordAgain={handleMouseDownPasswordAgain}
                  handleMouseUpPasswordAgain={handleMouseUpPasswordAgain}
                  reloadCaptcha={reloadCaptcha}
                  loginRedirect={loginRedirect}
                  handleToggleSelect={handleToggleSelect}
                  setVisibleSecurityPol={(val) => { }}
                  setVisiblePrivacyPol={(val) => { }}
                />
              )}

              {currentStep === 2 && (
                <RegisterStep2
                  localRegisterInputs={localRegisterInputs}
                  handleChange={handleChange}
                  fieldErrors={fieldErrors}
                  handleBackToStep1={handleBackToStep1}
                  registerClick={handleRegisterSubmit}
                  isButtonDisabled={() => validations.isButtonDisabled(localRegisterInputs, 2)}
                />
              )}
            </Col>
          )}

          {smsVerificationView && (
            <Col lg="5">
              <SmsVerification
                phoneNumber={localRegisterInputs.phone ? localRegisterInputs.phone.replace(/\D/g, "") : ""}
                email={localRegisterInputs.email}
              />
            </Col>
          )}
        </Row>
      </Container>
    </div>
  );
};

export default MainRegister;
